import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSearchParams } from "react-router-dom";

import cars from "../data/cars";
import { supabase } from "../lib/supabase";
import { useLanguage } from "../context/LanguageContext";

function Booking() {
  const { language } = useLanguage();

  const [searchParams] = useSearchParams();

  const preferredCarId = Number(
    searchParams.get("car")
  );

  const [pickupDate, setPickupDate] =
    useState("");

  const [pickupTime, setPickupTime] =
    useState("10:00");

  const [returnDate, setReturnDate] =
    useState("");

  const [returnTime, setReturnTime] =
    useState("10:00");

  const [availableCars, setAvailableCars] =
    useState([]);

  const [selectedCarId, setSelectedCarId] =
    useState("");

  const [customerName, setCustomerName] =
    useState("");

  const [customerPhone, setCustomerPhone] =
    useState("");

  const [
    availabilityChecked,
    setAvailabilityChecked,
  ] = useState(false);

  const [
    checkingAvailability,
    setCheckingAvailability,
  ] = useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const translations = {
    en: {
      label: "BOOK YOUR CAR",
      title: "Check availability.",
      description:
        "Choose your pickup and return dates first. We will show you which vehicles are available.",

      tripDetails: "Trip Details",
      pickupDate: "Pickup Date",
      pickupTime: "Pickup Time",
      returnDate: "Return Date",
      returnTime: "Return Time",
      check: "Check Available Cars",
      checking: "Checking availability...",

      availableTitle: "Available Cars",
      availableText:
        "Choose one of the vehicles available for your selected period.",
      available: "available",
      of: "of",
      vehicles: "vehicles",
      car: "Car",
      perDay: "per day",
      select: "Select Car",
      selected: "Selected",

      noCars:
        "No cars are available for the selected dates and times. Please try another period.",

      preferredUnavailable:
        "The car you selected is not available for this period. Please choose another vehicle.",

      details: "Your Details",
      fullName: "Full Name",
      phone: "Phone Number",
      namePlaceholder: "Your full name",
      phonePlaceholder: "Your phone number",

      summary: "Booking Summary",
      pickup: "Pickup",
      return: "Return",
      chargedDays: "Charged Days",
      dailyPrice: "Daily Price",
      estimatedTotal: "Estimated Total",
      day: "day",
      days: "days",

      submit: "Send Booking Request",
      submitting: "Sending request...",

      missingDates:
        "Please select pickup and return dates and times.",

      invalidPeriod:
        "Return date and time must be after pickup date and time.",

      selectCar:
        "Please select an available car.",

      enterDetails:
        "Please enter your full name and phone number.",

      availabilityError:
        "Could not check availability. Please try again.",

      bookingError:
        "Could not send your booking request. Please try again.",

      becameUnavailable:
        "This car is no longer available for the selected period. Please check availability again.",

      luxury: "Luxury",
      economic: "Economic",

      whatsappHello:
        "Hello AutoRent Albania! I would like to request a booking.",
      whatsappCar: "Car",
      whatsappPickup: "Pickup",
      whatsappReturn: "Return",
      whatsappDays: "Charged days",
      whatsappTotal: "Estimated total",
      whatsappName: "Name",
      whatsappPhone: "Phone",
    },

    sq: {
      label: "REZERVO MAKINËN",
      title: "Kontrollo disponueshmërinë.",
      description:
        "Zgjidh fillimisht datën dhe orën e marrjes dhe kthimit. Ne do të të tregojmë makinat e disponueshme.",

      tripDetails: "Detajet e Udhëtimit",
      pickupDate: "Data e Marrjes",
      pickupTime: "Ora e Marrjes",
      returnDate: "Data e Kthimit",
      returnTime: "Ora e Kthimit",
      check: "Kontrollo Makinat e Disponueshme",
      checking:
        "Po kontrollojmë disponueshmërinë...",

      availableTitle:
        "Makinat e Disponueshme",
      availableText:
        "Zgjidh një nga makinat e disponueshme për periudhën tënde.",
      available: "të disponueshme",
      of: "nga",
      vehicles: "makina",
      car: "Makina",
      perDay: "në ditë",
      select: "Zgjidh Makinën",
      selected: "E Zgjedhur",

      noCars:
        "Nuk ka makina të disponueshme për datat dhe oraret e zgjedhura. Provo një periudhë tjetër.",

      preferredUnavailable:
        "Makina që zgjodhe nuk është e disponueshme për këtë periudhë. Zgjidh një makinë tjetër.",

      details: "Të Dhënat e Tua",
      fullName: "Emri dhe Mbiemri",
      phone: "Numri i Telefonit",
      namePlaceholder:
        "Emri dhe mbiemri",
      phonePlaceholder:
        "Numri i telefonit",

      summary:
        "Përmbledhja e Rezervimit",
      pickup: "Marrja",
      return: "Kthimi",
      chargedDays:
        "Ditët e Llogaritura",
      dailyPrice: "Çmimi Ditor",
      estimatedTotal:
        "Totali i Parashikuar",
      day: "ditë",
      days: "ditë",

      submit:
        "Dërgo Kërkesën e Rezervimit",
      submitting:
        "Po dërgohet kërkesa...",

      missingDates:
        "Zgjidh datën dhe orën e marrjes dhe kthimit.",

      invalidPeriod:
        "Data dhe ora e kthimit duhet të jenë pas marrjes.",

      selectCar:
        "Zgjidh një makinë të disponueshme.",

      enterDetails:
        "Vendos emrin, mbiemrin dhe numrin e telefonit.",

      availabilityError:
        "Disponueshmëria nuk mund të kontrollohej. Provo përsëri.",

      bookingError:
        "Kërkesa e rezervimit nuk mund të dërgohej. Provo përsëri.",

      becameUnavailable:
        "Kjo makinë nuk është më e disponueshme për periudhën e zgjedhur. Kontrollo përsëri disponueshmërinë.",

      luxury: "Luksoze",
      economic: "Ekonomike",

      whatsappHello:
        "Përshëndetje AutoRent Albania! Dëshiroj të bëj një kërkesë rezervimi.",
      whatsappCar: "Makina",
      whatsappPickup: "Marrja",
      whatsappReturn: "Kthimi",
      whatsappDays:
        "Ditët e llogaritura",
      whatsappTotal:
        "Totali i parashikuar",
      whatsappName: "Emri",
      whatsappPhone: "Telefoni",
    },
  };

  const t =
    translations[language] ||
    translations.en;

  const timeOptions = useMemo(() => {
    const times = [];

    for (
      let hour = 0;
      hour < 24;
      hour++
    ) {
      for (
        let minute = 0;
        minute < 60;
        minute += 15
      ) {
        const formattedHour =
          String(hour).padStart(
            2,
            "0"
          );

        const formattedMinute =
          String(minute).padStart(
            2,
            "0"
          );

        times.push(
          `${formattedHour}:${formattedMinute}`
        );
      }
    }

    return times;
  }, []);

  const selectedCar =
    cars.find(
      (car) =>
        Number(car.id) ===
        Number(selectedCarId)
    );

  useEffect(() => {
    setAvailabilityChecked(false);
    setAvailableCars([]);
    setSelectedCarId("");
    setMessage("");
  }, [
    pickupDate,
    pickupTime,
    returnDate,
    returnTime,
  ]);

  const createDateTime = (
    date,
    time
  ) => {
    if (!date || !time) {
      return new Date(NaN);
    }

    const cleanTime =
      String(time).trim();

    const normalizedTime =
      cleanTime.split(":").length ===
      2
        ? `${cleanTime}:00`
        : cleanTime;

    return new Date(
      `${date}T${normalizedTime}`
    );
  };

  const formatDate = (
    dateString
  ) => {
    if (!dateString) {
      return "";
    }

    const [
      year,
      month,
      day,
    ] = dateString.split("-");

    return `${day}/${month}/${year}`;
  };

  const calculateRentalDays =
    () => {
      if (
        !pickupDate ||
        !pickupTime ||
        !returnDate ||
        !returnTime
      ) {
        return 0;
      }

      const pickup =
        createDateTime(
          pickupDate,
          pickupTime
        );

      const dropoff =
        createDateTime(
          returnDate,
          returnTime
        );

      const pickupDay =
        new Date(
          pickup.getFullYear(),
          pickup.getMonth(),
          pickup.getDate()
        );

      const returnDay =
        new Date(
          dropoff.getFullYear(),
          dropoff.getMonth(),
          dropoff.getDate()
        );

      const differenceInDays =
        Math.round(
          (returnDay -
            pickupDay) /
            (
              1000 *
              60 *
              60 *
              24
            )
        );

      const pickupMinutes =
        pickup.getHours() *
          60 +
        pickup.getMinutes();

      const returnMinutes =
        dropoff.getHours() *
          60 +
        dropoff.getMinutes();

      let rentalDays =
        differenceInDays + 1;

      if (
        pickupMinutes >=
        22 * 60
      ) {
        rentalDays -= 1;
      }

      if (
        returnMinutes <
        9 * 60
      ) {
        rentalDays -= 1;
      }

      return Math.max(
        1,
        rentalDays
      );
    };

  const rentalDays =
    calculateRentalDays();

  const estimatedTotal =
    selectedCar
      ? rentalDays *
        selectedCar.price
      : 0;

  const validatePeriod =
    () => {
      if (
        !pickupDate ||
        !pickupTime ||
        !returnDate ||
        !returnTime
      ) {
        setMessage(
          t.missingDates
        );

        return false;
      }

      const pickup =
        createDateTime(
          pickupDate,
          pickupTime
        );

      const dropoff =
        createDateTime(
          returnDate,
          returnTime
        );

      if (
        Number.isNaN(
          pickup.getTime()
        ) ||
        Number.isNaN(
          dropoff.getTime()
        )
      ) {
        setMessage(
          t.invalidPeriod
        );

        return false;
      }

      if (
        dropoff <= pickup
      ) {
        setMessage(
          t.invalidPeriod
        );

        return false;
      }

      return true;
    };

  const findBlockedCarIds = (
    bookings
  ) => {
    const requestedPickup =
      createDateTime(
        pickupDate,
        pickupTime
      );

    const requestedReturn =
      createDateTime(
        returnDate,
        returnTime
      );

    const blockedIds =
      new Set();

    bookings.forEach(
      (booking) => {
        const existingPickup =
          createDateTime(
            booking.pickup_date,
            booking.pickup_time
          );

        const existingReturn =
          createDateTime(
            booking.return_date,
            booking.return_time
          );

        if (
          Number.isNaN(
            existingPickup.getTime()
          ) ||
          Number.isNaN(
            existingReturn.getTime()
          )
        ) {
          return;
        }

        const overlaps =
          existingPickup <
            requestedReturn &&
          existingReturn >
            requestedPickup;

        if (overlaps) {
          blockedIds.add(
            Number(
              booking.car_id
            )
          );
        }
      }
    );

    return blockedIds;
  };

  const loadConflictingBookings =
    async () => {
      const {
        data,
        error,
      } = await supabase
        .from("bookings")
        .select(`
          id,
          car_id,
          pickup_date,
          pickup_time,
          return_date,
          return_time,
          status
        `)
        .eq(
          "status",
          "confirmed"
        )
        .lte(
          "pickup_date",
          returnDate
        )
        .gte(
          "return_date",
          pickupDate
        );

      if (error) {
        console.error(
          "Availability query error:",
          error
        );

        throw error;
      }

      return data || [];
    };

  const checkAvailability =
    async () => {
      setMessage("");

      if (
        !validatePeriod()
      ) {
        return;
      }

      setCheckingAvailability(
        true
      );

      try {
        const bookings =
          await loadConflictingBookings();

        const blockedCarIds =
          findBlockedCarIds(
            bookings
          );

        const freeCars =
          cars.filter(
            (car) =>
              !blockedCarIds.has(
                Number(car.id)
              )
          );

        setAvailableCars(
          freeCars
        );

        setAvailabilityChecked(
          true
        );

        if (
          preferredCarId &&
          freeCars.some(
            (car) =>
              Number(car.id) ===
              preferredCarId
          )
        ) {
          setSelectedCarId(
            String(
              preferredCarId
            )
          );
        } else if (
          preferredCarId &&
          cars.some(
            (car) =>
              Number(car.id) ===
              preferredCarId
          )
        ) {
          setSelectedCarId(
            ""
          );

          setMessage(
            t.preferredUnavailable
          );
        } else {
          setSelectedCarId(
            ""
          );
        }
      } catch (error) {
        console.error(
          "Could not check availability:",
          error
        );

        setAvailableCars(
          []
        );

        setAvailabilityChecked(
          false
        );

        setMessage(
          t.availabilityError
        );
      } finally {
        setCheckingAvailability(
          false
        );
      }
    };

  const checkSelectedCarAgain =
    async () => {
      const bookings =
        await loadConflictingBookings();

      const blockedCarIds =
        findBlockedCarIds(
          bookings
        );

      return !blockedCarIds.has(
        Number(
          selectedCarId
        )
      );
    };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    setMessage("");

    if (
      !validatePeriod()
    ) {
      return;
    }

    if (!selectedCar) {
      setMessage(
        t.selectCar
      );

      return;
    }

    if (
      !customerName.trim() ||
      !customerPhone.trim()
    ) {
      setMessage(
        t.enterDetails
      );

      return;
    }

    setSubmitting(
      true
    );

    try {
      const stillAvailable =
        await checkSelectedCarAgain();

      if (
        !stillAvailable
      ) {
        setAvailabilityChecked(
          false
        );

        setAvailableCars(
          []
        );

        setSelectedCarId(
          ""
        );

        setMessage(
          t.becameUnavailable
        );

        return;
      }

      const { error } =
        await supabase
          .from("bookings")
          .insert([
            {
              car_id:
                selectedCar.id,

              customer_name:
                customerName.trim(),

              customer_phone:
                customerPhone.trim(),

              pickup_date:
                pickupDate,

              pickup_time:
                pickupTime,

              return_date:
                returnDate,

              return_time:
                returnTime,

              status:
                "pending",
            },
          ]);

      if (error) {
        throw error;
      }

      const whatsappMessage =
        [
          t.whatsappHello,
          "",

          `${t.whatsappCar}: ${selectedCar.name} #${selectedCar.id}`,

          `${t.whatsappPickup}: ${formatDate(
            pickupDate
          )} ${pickupTime}`,

          `${t.whatsappReturn}: ${formatDate(
            returnDate
          )} ${returnTime}`,

          `${t.whatsappDays}: ${rentalDays}`,

          `${t.whatsappTotal}: €${estimatedTotal}`,

          "",

          `${t.whatsappName}: ${customerName.trim()}`,

          `${t.whatsappPhone}: ${customerPhone.trim()}`,
        ].join("\n");

      const whatsappUrl =
        `https://wa.me/355697555461?text=${encodeURIComponent(
          whatsappMessage
        )}`;

      window.location.href =
        whatsappUrl;
    } catch (error) {
      console.error(
        "Booking error:",
        error
      );

      setMessage(
        t.bookingError
      );
    } finally {
      setSubmitting(
        false
      );
    }
  };

  const translateCategory = (
    category
  ) => {
    if (
      category === "Luxury"
    ) {
      return t.luxury;
    }

    if (
      category === "Economic"
    ) {
      return t.economic;
    }

    return category;
  };

  return (
    <div
      style={{
        background:
          "#f5f5f5",
        minHeight:
          "100vh",
        padding:
          "60px 20px 90px",
      }}
    >
      <div
        style={{
          width:
            "min(1100px, 100%)",
          margin:
            "0 auto",
        }}
      >
        <div
          style={{
            maxWidth:
              "760px",
            marginBottom:
              "40px",
          }}
        >
          <p
            style={{
              margin:
                "0 0 10px",
              color:
                "#777",
              fontSize:
                "12px",
              fontWeight:
                "900",
              letterSpacing:
                "2px",
            }}
          >
            {t.label}
          </p>

          <h1
            style={{
              margin:
                "0 0 16px",
              fontSize:
                "clamp(40px, 6vw, 64px)",
              letterSpacing:
                "-3px",
              lineHeight:
                "1",
            }}
          >
            {t.title}
          </h1>

          <p
            style={{
              margin: 0,
              color:
                "#666",
              fontSize:
                "17px",
              lineHeight:
                "1.7",
            }}
          >
            {t.description}
          </p>
        </div>

        <section
          style={{
            background:
              "#ffffff",
            padding:
              "26px",
            borderRadius:
              "20px",
            border:
              "1px solid #e5e5e5",
            marginBottom:
              "28px",
          }}
        >
          <h2
            style={{
              margin:
                "0 0 22px",
              fontSize:
                "22px",
            }}
          >
            {t.tripDetails}
          </h2>

          <div
            style={{
              display:
                "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(210px, 1fr))",
              gap:
                "16px",
            }}
          >
            <Field
              label={
                t.pickupDate
              }
            >
              <input
                type="date"
                lang="en-GB"
                value={
                  pickupDate
                }
                onChange={(
                  event
                ) =>
                  setPickupDate(
                    event.target
                      .value
                  )
                }
                style={
                  inputStyle
                }
              />
            </Field>

            <Field
              label={
                t.pickupTime
              }
            >
              <select
                value={
                  pickupTime
                }
                onChange={(
                  event
                ) =>
                  setPickupTime(
                    event.target
                      .value
                  )
                }
                style={
                  inputStyle
                }
              >
                {timeOptions.map(
                  (time) => (
                    <option
                      key={
                        time
                      }
                      value={
                        time
                      }
                    >
                      {time}
                    </option>
                  )
                )}
              </select>
            </Field>

            <Field
              label={
                t.returnDate
              }
            >
              <input
                type="date"
                lang="en-GB"
                value={
                  returnDate
                }
                onChange={(
                  event
                ) =>
                  setReturnDate(
                    event.target
                      .value
                  )
                }
                style={
                  inputStyle
                }
              />
            </Field>

            <Field
              label={
                t.returnTime
              }
            >
              <select
                value={
                  returnTime
                }
                onChange={(
                  event
                ) =>
                  setReturnTime(
                    event.target
                      .value
                  )
                }
                style={
                  inputStyle
                }
              >
                {timeOptions.map(
                  (time) => (
                    <option
                      key={
                        time
                      }
                      value={
                        time
                      }
                    >
                      {time}
                    </option>
                  )
                )}
              </select>
            </Field>
          </div>

          <button
            type="button"
            onClick={
              checkAvailability
            }
            disabled={
              checkingAvailability
            }
            style={{
              marginTop:
                "20px",
              width:
                "100%",
              padding:
                "15px",
              border:
                "none",
              borderRadius:
                "12px",
              background:
                "#111317",
              color:
                "#ffffff",
              fontWeight:
                "900",
              cursor:
                checkingAvailability
                  ? "default"
                  : "pointer",
              opacity:
                checkingAvailability
                  ? 0.7
                  : 1,
            }}
          >
            {checkingAvailability
              ? t.checking
              : t.check}
          </button>
        </section>

        {message && (
          <div
            style={{
              marginBottom:
                "24px",
              padding:
                "15px 17px",
              borderRadius:
                "12px",
              background:
                "#fff4e5",
              border:
                "1px solid #f0d4aa",
              color:
                "#6b4b18",
              fontWeight:
                "700",
              lineHeight:
                "1.5",
            }}
          >
            {message}
          </div>
        )}

        {availabilityChecked && (
          <section
            style={{
              marginBottom:
                "30px",
            }}
          >
            <div
              style={{
                marginBottom:
                  "20px",
              }}
            >
              <h2
                style={{
                  margin:
                    "0 0 8px",
                  fontSize:
                    "28px",
                }}
              >
                {t.availableTitle}
              </h2>

              <p
                style={{
                  margin:
                    "0 0 5px",
                  color:
                    "#666",
                  lineHeight:
                    "1.6",
                }}
              >
                {t.availableText}
              </p>

              <strong
                style={{
                  fontSize:
                    "14px",
                }}
              >
                {
                  availableCars.length
                }{" "}
                {t.of}{" "}
                {cars.length}{" "}
                {t.vehicles}{" "}
                {t.available}
              </strong>
            </div>

            {availableCars.length ===
            0 ? (
              <div
                style={{
                  padding:
                    "24px",
                  borderRadius:
                    "18px",
                  background:
                    "#ffffff",
                  border:
                    "1px solid #e5e5e5",
                  color:
                    "#666",
                }}
              >
                {t.noCars}
              </div>
            ) : (
              <div
                style={{
                  display:
                    "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(270px, 1fr))",
                  gap:
                    "18px",
                }}
              >
                {availableCars.map(
                  (car) => {
                    const isSelected =
                      Number(
                        selectedCarId
                      ) ===
                      Number(
                        car.id
                      );

                    return (
                      <article
                        key={
                          car.id
                        }
                        style={{
                          background:
                            "#ffffff",
                          borderRadius:
                            "18px",
                          overflow:
                            "hidden",
                          border:
                            isSelected
                              ? "2px solid #111317"
                              : "1px solid #e5e5e5",
                        }}
                      >
                        <div
                          style={{
                            height:
                              "180px",
                            background:
                              "#e9e9e9",
                          }}
                        >
                          {car.images &&
                          car.images
                            .length >
                            0 ? (
                            <img
                              src={
                                car
                                  .images[0]
                              }
                              alt={
                                car.name
                              }
                              style={{
                                width:
                                  "100%",
                                height:
                                  "100%",
                                objectFit:
                                  "cover",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                height:
                                  "100%",
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                justifyContent:
                                  "center",
                                color:
                                  "#888",
                              }}
                            >
                              {language ===
                              "sq"
                                ? "Foto së shpejti"
                                : "Photo coming soon"}
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            padding:
                              "18px",
                          }}
                        >
                          <div
                            style={{
                              display:
                                "flex",
                              justifyContent:
                                "space-between",
                              gap:
                                "15px",
                            }}
                          >
                            <div>
                              <strong
                                style={{
                                  display:
                                    "block",
                                  fontSize:
                                    "18px",
                                }}
                              >
                                {
                                  car.name
                                }
                              </strong>

                              <span
                                style={{
                                  color:
                                    "#888",
                                  fontSize:
                                    "12px",
                                }}
                              >
                                {
                                  t.car
                                }{" "}
                                #
                                {
                                  car.id
                                }
                              </span>
                            </div>

                            <strong>
                              €
                              {
                                car.price
                              }

                              <span
                                style={{
                                  display:
                                    "block",
                                  color:
                                    "#888",
                                  fontSize:
                                    "10px",
                                  fontWeight:
                                    "600",
                                }}
                              >
                                {
                                  t.perDay
                                }
                              </span>
                            </strong>
                          </div>

                          <p
                            style={{
                              color:
                                "#666",
                              fontSize:
                                "13px",
                              margin:
                                "12px 0",
                            }}
                          >
                            {
                              car.engine
                            }{" "}
                            •{" "}
                            {
                              car.transmission
                            }{" "}
                            •{" "}
                            {translateCategory(
                              car.category
                            )}
                          </p>

                          <button
                            type="button"
                            onClick={() =>
                              setSelectedCarId(
                                String(
                                  car.id
                                )
                              )
                            }
                            style={{
                              width:
                                "100%",
                              padding:
                                "12px",
                              border:
                                "none",
                              borderRadius:
                                "11px",
                              background:
                                isSelected
                                  ? "#111317"
                                  : "#efefef",
                              color:
                                isSelected
                                  ? "#ffffff"
                                  : "#111317",
                              fontWeight:
                                "900",
                              cursor:
                                "pointer",
                            }}
                          >
                            {isSelected
                              ? t.selected
                              : t.select}
                          </button>
                        </div>
                      </article>
                    );
                  }
                )}
              </div>
            )}
          </section>
        )}

        {selectedCar && (
          <form
            onSubmit={
              handleSubmit
            }
          >
            <section
              style={{
                background:
                  "#ffffff",
                padding:
                  "26px",
                borderRadius:
                  "20px",
                border:
                  "1px solid #e5e5e5",
                marginBottom:
                  "22px",
              }}
            >
              <h2
                style={{
                  margin:
                    "0 0 22px",
                  fontSize:
                    "22px",
                }}
              >
                {t.details}
              </h2>

              <div
                style={{
                  display:
                    "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap:
                    "16px",
                }}
              >
                <Field
                  label={
                    t.fullName
                  }
                >
                  <input
                    type="text"
                    value={
                      customerName
                    }
                    onChange={(
                      event
                    ) =>
                      setCustomerName(
                        event.target
                          .value
                      )
                    }
                    placeholder={
                      t.namePlaceholder
                    }
                    style={
                      inputStyle
                    }
                  />
                </Field>

                <Field
                  label={
                    t.phone
                  }
                >
                  <input
                    type="tel"
                    value={
                      customerPhone
                    }
                    onChange={(
                      event
                    ) =>
                      setCustomerPhone(
                        event.target
                          .value
                      )
                    }
                    placeholder={
                      t.phonePlaceholder
                    }
                    style={
                      inputStyle
                    }
                  />
                </Field>
              </div>
            </section>

            <section
              style={{
                background:
                  "#111317",
                color:
                  "#ffffff",
                padding:
                  "26px",
                borderRadius:
                  "20px",
              }}
            >
              <h2
                style={{
                  margin:
                    "0 0 22px",
                  fontSize:
                    "22px",
                }}
              >
                {t.summary}
              </h2>

              <div
                style={{
                  display:
                    "grid",
                  gap:
                    "12px",
                  marginBottom:
                    "22px",
                }}
              >
                <SummaryRow
                  label={
                    t.car
                  }
                  value={`${selectedCar.name} #${selectedCar.id}`}
                />

                <SummaryRow
                  label={
                    t.pickup
                  }
                  value={`${formatDate(
                    pickupDate
                  )} ${pickupTime}`}
                />

                <SummaryRow
                  label={
                    t.return
                  }
                  value={`${formatDate(
                    returnDate
                  )} ${returnTime}`}
                />

                <SummaryRow
                  label={
                    t.dailyPrice
                  }
                  value={`€${selectedCar.price}`}
                />

                <SummaryRow
                  label={
                    t.chargedDays
                  }
                  value={`${rentalDays} ${
                    rentalDays ===
                    1
                      ? t.day
                      : t.days
                  }`}
                />

                <SummaryRow
                  label={
                    t.estimatedTotal
                  }
                  value={`€${estimatedTotal}`}
                  strong
                />
              </div>

              <button
                type="submit"
                disabled={
                  submitting
                }
                style={{
                  width:
                    "100%",
                  padding:
                    "16px",
                  border:
                    "none",
                  borderRadius:
                    "12px",
                  background:
                    "#ffffff",
                  color:
                    "#111317",
                  fontWeight:
                    "900",
                  cursor:
                    submitting
                      ? "default"
                      : "pointer",
                  opacity:
                    submitting
                      ? 0.7
                      : 1,
                }}
              >
                {submitting
                  ? t.submitting
                  : t.submit}
              </button>
            </section>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}) {
  return (
    <label
      style={{
        display:
          "block",
      }}
    >
      <span
        style={{
          display:
            "block",
          marginBottom:
            "7px",
          fontSize:
            "13px",
          fontWeight:
            "800",
        }}
      >
        {label}
      </span>

      {children}
    </label>
  );
}

function SummaryRow({
  label,
  value,
  strong = false,
}) {
  return (
    <div
      style={{
        display:
          "flex",
        justifyContent:
          "space-between",
        gap:
          "20px",
        paddingBottom:
          "11px",
        borderBottom:
          "1px solid rgba(255,255,255,0.09)",
      }}
    >
      <span
        style={{
          color:
            "#9da2a9",
          fontSize:
            "14px",
        }}
      >
        {label}
      </span>

      <strong
        style={{
          textAlign:
            "right",
          fontSize:
            strong
              ? "19px"
              : "14px",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding:
    "13px 14px",
  borderRadius:
    "11px",
  border:
    "1px solid #d8d8d8",
  background:
    "#ffffff",
  color:
    "#111317",
  outline:
    "none",
};

export default Booking;